import React, { useState, useEffect, useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  FaTimes,
  FaPlay,
  FaPlus,
  FaMinus,
  FaPause,
  FaClock,
  FaStop,
  FaSave,
  FaTrash,
} from "react-icons/fa";
import { useUser } from "./UserContext";
import "./css/PianoRollModal.css";
import InstrumentAudioContext from "./InstrumentAudioContext";
import { ClipLoader } from "react-spinners";
import { debounce } from "lodash";
import { usePatternInsSeq } from "./PatternInsSeqContext";
import { toast, ToastContainer } from "react-toastify";

const generateNotes = () => {
  const notes = [];
  const noteNames = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  for (let octave = 0; octave < 6; octave++) {
    for (const note of noteNames) {
      notes.push(`${note}${octave}`);
    }
  }
  return notes; // Reverse to start from C0 at the bottom
};

const PianoRollModal = ({
  instrument,
  initialPatternNo,
  onClose,
  setPianoRollIsPlaying,
  bpm,
  setBpm,
  selectedInstruments,
  setSelectedInstruments,
  initialRagaGrid,
  globalPlayingNotes = [],
  globalCurrentBar = null,
  onLivePreviewGridChange,
  stepCount = 16,
}) => {
  const notes = generateNotes();
  const [currentBar, setCurrentBar] = useState(0);
  const intervalRef = useRef(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [volume, setVolume] = useState(
    InstrumentAudioContext.getInstVolume(instrument),
  );
  const { user } = useUser();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMetronomeOn, setIsMetronomeOn] = useState(false);
  const [currentMetronomeBeat, setCurrentMetronomeBeat] = useState(0);
  const [selectedPianoRollPattern, setSelectedPianoRollPattern] = useState("");
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isVolumeChanging, setIsVolumeChanging] = useState(false);
  const [pianoRollPatterns, setPianoRollPatterns] = useState([]);
  const [playingNotes, setPlayingNotes] = useState([]);
  const {
    fetchPianoRollPatterns,
    fetchNotesPianoRoll,
    savePianoRollPattern,
    deletePianoRollPattern,
  } = usePatternInsSeq();
  const [grid, setGrid] = useState(
    Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false)),
  );
  useEffect(() => {
    if (!initialRagaGrid) return;

    if (initialRagaGrid.grid) {
      setGrid(expandGridToStepCount(initialRagaGrid.grid));
      setCurrentBar(0);
      setSelectedPianoRollPattern(initialRagaGrid.patternNo || "");
      return;
    }

    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    if (initialRagaGrid.type === "chords") {
      initialRagaGrid.chordNotes.forEach(({ note, step }) => {
        const rowIndex = notes.findIndex((n) => n === note);

        if (rowIndex !== -1 && step >= 0 && step < stepCount) {
          newGrid[step][rowIndex] = true;
        }
      });
    } else if (initialRagaGrid.notes?.length) {
      initialRagaGrid.notes.forEach((noteName, colIndex) => {
        const rowIndex = notes.findIndex((n) => n === noteName);

        if (rowIndex !== -1 && colIndex < stepCount) {
          newGrid[colIndex][rowIndex] = true;
        }
      });
    }

    setGrid(newGrid);
    setCurrentBar(0);
    setSelectedPianoRollPattern("");
  }, [initialRagaGrid]);

  useEffect(() => {
    setPlayingNotes(globalPlayingNotes || []);
  }, [globalPlayingNotes]);
  useEffect(() => {
    if (globalCurrentBar !== null && globalCurrentBar >= 0) {
      setCurrentBar(globalCurrentBar);
    }
  }, [globalCurrentBar]);
  useEffect(() => {
    const fetchPatterns = async () => {
      const patterns = await fetchPianoRollPatterns();
      setPianoRollPatterns(patterns);
    };

    fetchPatterns();
  }, [fetchPianoRollPatterns]);
  useEffect(() => {
    if (initialPatternNo) {
      setSelectedPianoRollPattern(initialPatternNo);
      handlePianoRollPatternChange({ target: { value: initialPatternNo } });
    }
  }, [initialPatternNo]);
  useEffect(() => {
    InstrumentAudioContext.setInstVolume(volume, instrument);
  }, [volume, instrument]);

  useEffect(() => {
    if (setPianoRollIsPlaying) {
      setPianoRollIsPlaying(isPlaying);
    }
  }, [isPlaying, setPianoRollIsPlaying]);

  useEffect(() => {
    InstrumentAudioContext.loadAudio(instrument);
    return () => {
      InstrumentAudioContext.stopAllNotes();
    };
  }, [instrument]);

  useEffect(() => {
    InstrumentAudioContext.setBpm(bpm);
  }, [bpm]);

  useEffect(() => {
    if (isPlaying) {
      const interval = (60 / bpm) * 250;
      intervalRef.current = setInterval(() => {
        setCurrentBar((prevBar) => (prevBar + 1) % stepCount);
        if (isMetronomeOn) {
          setCurrentMetronomeBeat((prevBeat) => (prevBeat % 4) + 1);
          InstrumentAudioContext.playMetronomeSound(currentMetronomeBeat);
        }
      }, interval);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, bpm, isMetronomeOn, currentMetronomeBeat]);

  useEffect(() => {
    if (!isPlaying || isVolumeChanging || !grid[currentBar]) return;

    const activeRows = grid[currentBar]
      .map((active, rowIndex) => (active ? rowIndex : null))
      .filter((rowIndex) => rowIndex !== null);

    activeRows.forEach((rowIndex) => {
      const adjustedNoteIndex = rowIndex;
      InstrumentAudioContext.playPianoRollNote(
        adjustedNoteIndex,
        notes,
        instrument,
        900,
      );
    });
  }, [currentBar, isPlaying, grid, notes, instrument, isVolumeChanging]);
  const handleMouseDown = (rowIndex, colIndex) => {
    setIsMouseDown(true);
    toggleCell(rowIndex, colIndex);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
    InstrumentAudioContext.stopAllNotes();
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (element && element.dataset.row && element.dataset.col) {
      const rowIndex = parseInt(element.dataset.row, 10);
      const colIndex = parseInt(element.dataset.col, 10);
      toggleCell(rowIndex, colIndex);
    }
  };

  const handleTouchEnd = () => {
    setIsMouseDown(false);
    InstrumentAudioContext.stopAllNotes();
  };

  const toggleCell = (rowIndex, colIndex) => {
    const newGrid = grid.map((row, rIdx) =>
      row.map((cell, cIdx) =>
        rIdx === colIndex && cIdx === rowIndex ? !cell : cell,
      ),
    );
    setGrid(newGrid);

    const noteIndex = rowIndex;
    InstrumentAudioContext.playNoteOnMouseDown(noteIndex, notes, instrument);
  };
  const expandGridToStepCount = (gridToExpand) =>
    Array(stepCount)
      .fill()
      .map(
        (_, index) =>
          gridToExpand[index % gridToExpand.length] ||
          Array(notes.length).fill(false),
      );
  const increaseBpm = () => setBpm((prev) => Math.min(prev + 5, 300));
  const decreaseBpm = () => setBpm((prev) => Math.max(prev - 5, 20));
  const clearGrid = () => {
    const newGrid = emptyGrid();
    updateGridLive(newGrid);
    setSelectedPianoRollPattern("");
  };
  const emptyGrid = () =>
    Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

  const updateGridLive = (newGrid) => {
    setGrid(newGrid);
    onLivePreviewGridChange?.(instrument, newGrid);
  };
  const debouncedSetVolume = useCallback(
    debounce((volume) => {
      InstrumentAudioContext.setInstVolume(volume, instrument);
      setIsVolumeChanging(false);
    }, 100),
    [instrument],
  );

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setIsVolumeChanging(true);
    debouncedSetVolume(newVolume);
  };

  const toggleMetronome = () => {
    setIsMetronomeOn((prev) => !prev);
    setCurrentMetronomeBeat(0);
  };

  const isNaturalNote = (note) => {
    return !note.includes("#");
  };

  const handleClose = () => {
    onClose(grid, selectedPianoRollPattern);
  };

  const handleStopClick = () => {
    setIsPlaying(false);
    setCurrentBar(0);
  };

  const handlePianoRollPatternChange = async (e) => {
    const patternNo = e.target.value;

    if (!patternNo) {
      setSelectedPianoRollPattern("");
      return; // don't clear grid, just make Save create new pattern
    }

    setSelectedPianoRollPattern(patternNo);
    const notesArray = await fetchNotesPianoRoll(patternNo);
    console.log(`Notes for pattern ${patternNo}:`, notesArray.join(", "));

    const newGrid = Array(stepCount)
      .fill()
      .map(() => Array(notes.length).fill(false));

    notesArray.forEach((note) => {
      const [noteName, bar] = note.split(",");
      const rowIndex = notes.findIndex((n) => n === noteName);
      const colIndex = parseInt(bar, 10) - 1;
      if (rowIndex !== -1 && colIndex >= 0 && colIndex < stepCount) {
        newGrid[colIndex][rowIndex] = true;
      }
    });

    setGrid(newGrid);

    const updatedInstruments = selectedInstruments.map((inst) =>
      inst.InsName === instrument
        ? { ...inst, PianoRollPatternNo: patternNo }
        : inst,
    );
    setSelectedInstruments(updatedInstruments);
  };

  const handleSavePattern = async () => {
    setIsLoadingSave(true);

    try {
      const isTempPattern =
        String(selectedPianoRollPattern).startsWith("melody-") ||
        String(selectedPianoRollPattern).startsWith("loaded-");

      let patternNoToSave = selectedPianoRollPattern;

      if (!selectedPianoRollPattern || isTempPattern) {
        const latestPatterns = await fetchPianoRollPatterns();

        const maxPatternNo = latestPatterns.reduce(
          (max, pattern) => Math.max(max, Number(pattern.patternNo) || 0),
          0,
        );

        patternNoToSave = maxPatternNo + 1;
      }

      await savePianoRollPattern(
        patternNoToSave,
        grid,
        notes,
        setSelectedPianoRollPattern,
      );

      const patterns = await fetchPianoRollPatterns();
      setPianoRollPatterns(patterns);
      setSelectedPianoRollPattern(String(patternNoToSave));

      setSelectedInstruments((prev) =>
        prev.map((inst) =>
          inst.InsName === instrument
            ? {
                ...inst,
                PianoRollPatternNo: String(patternNoToSave),
                PianoRollPatternName: "",
              }
            : inst,
        ),
      );

      toast.success(`Saved as Piano Roll Pattern ${patternNoToSave}`);
    } catch (error) {
      toast.error("Failed to save piano roll pattern");
    } finally {
      setIsLoadingSave(false);
    }
  };
  const handleDeletePattern = async () => {
    const parsedPatternNo = parseInt(selectedPianoRollPattern, 10);

    if (!parsedPatternNo || parsedPatternNo === 0) {
      toast.warning("Invalid pattern number.");
      return;
    }

    if (window.confirm("Are you sure you want to delete this pattern?")) {
      setIsLoadingDelete(true);
      try {
        await deletePianoRollPattern(parsedPatternNo);
        const patterns = await fetchPianoRollPatterns();
        setPianoRollPatterns(patterns);
        setSelectedPianoRollPattern(""); // Reset the dropdown value to default

        // Clear the grid
        setGrid(
          Array(stepCount)
            .fill()
            .map(() => Array(notes.length).fill(false)),
        );
      } catch (error) {
        toast.error("Failed to delete pattern");
      } finally {
        setIsLoadingDelete(false);
      }
    }
  };
  const increaseOctave = () => {
    const newGrid = shiftOctave(grid, 1);
    updateGridLive(newGrid);
  };

  const decreaseOctave = () => {
    const newGrid = shiftOctave(grid, -1);
    updateGridLive(newGrid);
  };

  const shiftOctave = (grid, direction) => {
    const newGrid = Array.from({ length: 16 }, () =>
      Array(notes.length).fill(false),
    );
    for (let col = 0; col < grid.length; col++) {
      for (let row = 0; row < grid[col].length; row++) {
        if (grid[col][row]) {
          const note = notes[row];
          const noteName = note.slice(0, -1);
          let octave = parseInt(note.slice(-1), 10);
          octave += direction;
          const newNote = `${noteName}${octave}`;
          const newRowIndex = notes.findIndex((n) => n === newNote);
          if (newRowIndex !== -1) {
            newGrid[col][newRowIndex] = true;
          }
        }
      }
    }
    return newGrid;
  };
  const NOTE_NAMES = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];

  const transposeNote = (note, semitones) => {
    const match = note.match(/^([A-G]#?)(\d)$/);
    if (!match) return note;

    const [, noteName, octaveStr] = match;
    const octave = parseInt(octaveStr, 10);
    const noteIndex = NOTE_NAMES.indexOf(noteName);

    const absolute = octave * 12 + noteIndex + semitones;
    if (absolute < 0) return note;

    const newOctave = Math.floor(absolute / 12);
    const newNoteName = NOTE_NAMES[absolute % 12];

    return `${newNoteName}${newOctave}`;
  };

  const shiftGridSemitone = (direction) => {
    const newGrid = Array.from({ length: 16 }, () =>
      Array(notes.length).fill(false),
    );

    grid.forEach((bar, barIndex) => {
      bar.forEach((active, noteIndex) => {
        if (!active) return;

        const oldNote = notes[noteIndex];
        const newNote = transposeNote(oldNote, direction);
        const newNoteIndex = notes.findIndex((n) => n === newNote);

        if (newNoteIndex !== -1) {
          newGrid[barIndex][newNoteIndex] = true;
        }
      });
    });

    updateGridLive(newGrid);
  };

  return (
    <div
      className="piano-roll-modal"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchEnd={handleTouchEnd}
    >
      <div className="piano-roll-modal-content">
        <div className="piano-roll-header">
          <h2 className="piano-roll-title">{instrument} Piano Roll</h2>
          <button onClick={handleClose} className="close-button">
            <FaTimes />
          </button>
        </div>
        <div className="pianoroll-controls">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="play-button-seq"
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button onClick={handleStopClick} className="stop-button2">
            <FaStop />
          </button>
          <select
            className="roll-pattern-dropdown"
            value={selectedPianoRollPattern}
            onChange={handlePianoRollPatternChange}
          >
            <option value="">Select Preset</option>
            {pianoRollPatterns.map((pattern) => (
              <option key={pattern.$id} value={pattern.patternNo}>
                Preset: {pattern.patternNo}
              </option>
            ))}
          </select>
          <button
            onClick={handleSavePattern}
            className="save-pattern-button"
            disabled={isLoadingSave}
          >
            {isLoadingSave ? (
              <ClipLoader size={20} color={"#fff"} />
            ) : (
              <FaSave />
            )}
          </button>
          <button
            onClick={handleDeletePattern}
            className="delete-pattern-button"
            disabled={isLoadingDelete}
          >
            {isLoadingDelete ? (
              <ClipLoader size={20} color={"#fff"} />
            ) : (
              <FaTrash />
            )}
          </button>{" "}
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolumeChange}
            className="volume-slider-seq"
          />
          <button onClick={toggleMetronome} className="save-pattern-button">
            <FaClock />
          </button>
          <button onClick={clearGrid} className="clear-button">
            Clear
          </button>
          <div className="bpm-controls">
            <button onClick={decreaseBpm} className="bpm-button">
              <FaMinus />
            </button>
            <span className="bpm-display">{bpm} BPM</span>
            <button onClick={increaseBpm} className="bpm-button">
              <FaPlus />
            </button>
          </div>
          <div className="octave-controls">
            <button onClick={decreaseOctave} className="octave-button">
              <FaMinus />
            </button>
            <p> Oct </p>
            <button onClick={increaseOctave} className="octave-button">
              <FaPlus />
            </button>
          </div>
          <div className="octave-controls">
            <button
              onClick={() => shiftGridSemitone(-1)}
              className="octave-button"
            >
              <FaMinus />
            </button>
            <p> Tra </p>
            <button
              onClick={() => shiftGridSemitone(1)}
              className="octave-button"
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div
          className="piano-roll-table-container"
          onTouchMove={handleTouchMove}
        >
          <table className="piano-roll-table">
            <tbody>
              {[...notes].reverse().map((note) => {
                const rowIndex = notes.findIndex((n) => n === note);

                return (
                  <tr
                    key={rowIndex}
                    className={
                      isNaturalNote(note) ? "natural-note" : "sharp-note"
                    }
                  >
                    <td className="sticky-left">{note}</td>
                    {[...Array(stepCount)].map((_, colIndex) => (
                      <td
                        key={colIndex}
                        style={{ width: "50px" }}
                        className={`${
                          grid[colIndex] && grid[colIndex][rowIndex]
                            ? "active"
                            : ""
                        } ${
                          colIndex === currentBar &&
                          grid[colIndex] &&
                          grid[colIndex][rowIndex]
                            ? "playing"
                            : ""
                        } ${
                          [0, 4, 8, 12].includes(colIndex)
                            ? "highlighted-cell"
                            : ""
                        }`}
                        onMouseDown={() => handleMouseDown(rowIndex, colIndex)}
                        onTouchStart={() => handleMouseDown(rowIndex, colIndex)}
                        data-row={rowIndex}
                        data-col={colIndex}
                      ></td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th className="sticky-left corner-box">Keys</th>
                {[...Array(stepCount)].map((_, index) => (
                  <th
                    key={index}
                    style={{ width: "200px" }}
                    className={index === currentBar ? "current-bar" : ""}
                  >
                    {index + 1}
                  </th>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
      />
    </div>
  );
};

export default PianoRollModal;
